import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { backendURL } from "../url";
import { attributeReferralCode } from "../apis/referralApi";

interface AuthContextType {
  isLoggedIn: boolean;
  setIsLoggedIn: (value: boolean) => void;
  userId: string | null;
  login: (email: string, password: string) => Promise<any>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [userId, setUserId] = useState<string | null>(() => {
    return localStorage.getItem("userId");
  });
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => {
    return localStorage.getItem("isLoggedIn") === "true";
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUserId = localStorage.getItem("userId");
    if (token && storedUserId) {
      setIsLoggedIn(true);
      setUserId(storedUserId);
    } else {
      setIsLoggedIn(false);
      setUserId(null);
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post(
        `${backendURL}/api/auth/login`,
        // `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`,
        { email, password }
      );
      if (response.status === 200) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem("userType", response.data.userType);
        localStorage.setItem("userId", response.data.id);
        localStorage.setItem("userName", response.data.name);
        setUserId(response.data.id);
        setIsLoggedIn(true);

        // Bind a stored referral to this account at login, so opening the link
        // while logged out then signing in on /login still sticks — they may
        // never return to /memberships before buying weeks later.
        const pendingReferralCode = localStorage.getItem("pendingReferralCode");
        if (response.data.userType === "user" && pendingReferralCode) {
          try {
            await attributeReferralCode(pendingReferralCode);
          } catch {
            // A code that does not apply (already referred, not a new member)
            // must not block login.
          }
        }
      }
      return response.data;
    } catch (error) {
      console.error("Error logging in", error);
    }
  };

  const logout = async () => {
    console.log("Logout from auth context");
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("userId");
      localStorage.removeItem("userType");
      localStorage.removeItem("userName");

      setUserId(null);
      setIsLoggedIn(false);
    } catch (error) {
      console.error("Error logging out", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{ userId, login, logout, isLoggedIn, setIsLoggedIn }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const checkTokenExpiry = (token: string) => {
  if (!token) return true;
  if (token === "undefined") return true;
  if (token === "null") return true;
  if (token === "") return true;

  const payload = JSON.parse(atob(token.split(".")[1]));
  const expiryTime = payload.exp * 1000;
  const currentTime = Date.now();
  return currentTime > expiryTime;
};
